// web/src/lib/zitchAgent/_legs.ts —— 内部：客户腿 / 咨询专家腿分流（两腿操作必须带 call 号）
import type { ZitchEvent } from './types';

export interface ZitchLegs {
  callId: number;
  consultCallId: number;
  /** 忙时同事邀请腿：不得覆盖客户 callId，也不得当咨询第二路 */
  inviteCallId: number;
  /** 邀请已接通但客户腿还在（保持并接 / 挂断并接的窗口） */
  inviteUp: boolean;
  outDialing: boolean;
  consultDialing: boolean;
  /** 已点取消/整通挂断，但专家 call 号尚未下发：拿到号后立即 hangup 该腿 */
  consultCanceling: boolean;
  /** 已发出完成转接：缺号终态归客户腿，不得当咨询失败 */
  consultCompleting: boolean;
  held: boolean;
}

export function isConsultLeg(legs: ZitchLegs, call?: number): boolean {
  // 无 call 号不猜——客户腿终态缺号时若当成专家，会把客户通话留在条上
  if (!call) return false;
  return call === legs.consultCallId || (legs.consultDialing && call !== legs.callId);
}

function isConsultFailure(legs: ZitchLegs, call?: number): boolean {
  if (isConsultLeg(legs, call)) return true;
  if (legs.consultCompleting) return false;
  // 两腿并存时缺号终态优先归咨询，避免把客户腿拆掉
  return !call && legs.callId > 0 && (legs.consultDialing || legs.consultCallId > 0);
}

export function clearConsult(legs: ZitchLegs) {
  legs.consultCallId = 0;
  legs.consultDialing = false;
  legs.consultCanceling = false;
  legs.consultCompleting = false;
}

export function resetLegs(legs: ZitchLegs) {
  legs.callId = 0;
  legs.outDialing = false;
  legs.held = false;
  legs.inviteCallId = 0;
  legs.inviteUp = false;
  clearConsult(legs);
}

export function isInviteLeg(legs: ZitchLegs, call?: number): boolean {
  return !!call && call === legs.inviteCallId;
}

/**
 * 按 call 号分流引擎 state：咨询第二路不得覆盖客户腿，专家 ended 不得清客户通话。
 * 媒体启停由调用方负责（up 客户腿 / 客户腿终态）。
 */
export function applyCallState(
  legs: ZitchLegs,
  m: { state?: string; call?: number; reason?: string },
  emit: (ev: ZitchEvent) => void,
  onPrimaryUp: () => void,
  onPrimaryEnd: () => void,
): void {
  const c = m.call || 0;
  if (m.state === 'up') {
    if (isInviteLeg(legs, c)) {
      // 原客户已挂：邀请腿升为当前通话；原客户仍在（保持并接）则两腿并存，不覆盖客户号
      if (!legs.callId) {
        legs.callId = c;
        legs.inviteCallId = 0;
        legs.inviteUp = false;
        onPrimaryUp();
        emit({ kind: 'in-call', call: legs.callId });
      } else {
        legs.inviteUp = true;
      }
      return;
    }
    if (isConsultLeg(legs, c)) {
      if (c) legs.consultCallId = c;
      if (legs.consultCallId) legs.consultDialing = false;
      emit({ kind: 'consult-up', call: legs.consultCallId });
      return;
    }
    legs.callId = c || legs.callId;
    legs.outDialing = false;
    onPrimaryUp();
    emit({ kind: 'in-call', call: legs.callId });
    return;
  }
  if (m.state === 'dialing') {
    // 出局已拨出即下发 call 号——记录供「取消」下发 hangup（core 收到后向中继发 CANCEL）
    if (isInviteLeg(legs, c)) return; // 邀请不得覆盖客户 callId
    if (isConsultLeg(legs, c)) {
      if (c) legs.consultCallId = c;
      if (legs.consultCallId) legs.consultDialing = false;
      emit({ kind: 'consult-ringing', call: legs.consultCallId });
      return;
    }
    legs.callId = c || legs.callId;
    legs.outDialing = false;
    return;
  }
  if (m.state === 'ended' || m.state === 'transferred') {
    if (isInviteLeg(legs, c)) {
      const ended = legs.inviteCallId || c;
      legs.inviteCallId = 0;
      legs.inviteUp = false;
      emit({ kind: 'invite-ended', call: ended });
      return;
    }
    if (isConsultFailure(legs, c) && legs.callId) {
      const ended = legs.consultCallId || c;
      clearConsult(legs);
      emit({ kind: 'consult-ended', call: ended });
      return;
    }
    // 客户腿结束但专家腿可能还在：先收客户，保留咨询标记等拿到号再 hangup
    if (legs.callId && (!c || c === legs.callId) && (legs.consultCallId || legs.consultDialing || legs.inviteCallId)) {
      const ended = legs.callId;
      legs.callId = 0;
      legs.outDialing = false;
      legs.held = false;
      onPrimaryEnd();
      emit({ kind: 'ended', call: ended });
      // 挂断当前并接：邀请已接通才升为主通话；未接听的邀请只留卡片
      if (legs.inviteCallId && legs.inviteUp) {
        legs.callId = legs.inviteCallId;
        legs.inviteCallId = 0;
        legs.inviteUp = false;
        onPrimaryUp();
        emit({ kind: 'in-call', call: legs.callId });
      }
      return;
    }
    // 盲转 / 完成咨询转：本腿已被服务端释放（transferredOut 由 store 保留）
    const ended = legs.callId || c;
    resetLegs(legs);
    onPrimaryEnd();
    emit({ kind: 'ended', call: ended });
    return;
  }
  if (m.state === 'timeout') {
    if (isInviteLeg(legs, c)) {
      const ended = legs.inviteCallId || c;
      legs.inviteCallId = 0;
      legs.inviteUp = false;
      emit({ kind: 'invite-ended', call: ended });
      return;
    }
    if (isConsultFailure(legs, c) && legs.callId) {
      const ended = legs.consultCallId || c;
      clearConsult(legs);
      emit({ kind: 'consult-ended', call: ended });
      return;
    }
    // 服务端振铃超时已单方回收本腿（客户由 ACD 转派其他坐席）：本机必须收尾，
    // 否则状态停在 ringing-in，顶栏「来电」与振铃音会一直不停，点拒接也无对象可挂。
    // 只认与当前在途腿一致的超时——重派可能让同一坐席马上拿到新腿，晚到的旧超时不得清掉新通话。
    if (!c || c === legs.callId) {
      const ended = legs.callId;
      resetLegs(legs);
      onPrimaryEnd();
      emit({ kind: 'ring-timeout', call: ended });
    }
    return;
  }
  if (m.state === 'held') {
    if (!c || c === legs.callId) legs.held = true;
    emit({ kind: 'held', call: c, held: true });
    return;
  }
  if (m.state === 'active') {
    if (!c || c === legs.callId) legs.held = false;
    emit({ kind: 'held', call: c, held: false });
    return;
  }
  if (m.state === 'error') {
    if (isInviteLeg(legs, c)) {
      const ended = legs.inviteCallId || c;
      legs.inviteCallId = 0;
      legs.inviteUp = false;
      emit({ kind: 'invite-ended', call: ended });
      return;
    }
    if (isConsultFailure(legs, c) && legs.callId) {
      clearConsult(legs);
      emit({ kind: 'consult-failed', call: c || undefined, message: m.reason || 'dial failed' });
      return;
    }
    resetLegs(legs);
    emit({ kind: 'dial-failed', message: m.reason || 'dial failed' });
  }
}
